import i18n from '@/i18n';
import { Role as NxtRole, RoleKey } from '@nxtpeople/nxt-vue-library';
import { NavigationGuard, RouteLocationNormalized } from 'vue-router';
import { PaymentSystem, PaymentSystemKey } from '../collections/contracts-api/payment-systems/payment-system.interface';
import { isUserInfoReady } from '../collections/contracts-api/user-info/user-info.helper';
import { Tenant } from '../models/tenant.interface';
import store from '../store';

export interface Role extends Omit<NxtRole, 'key'> {
   key: RoleKey;
   name: string;
   description: string;
}

export function checkPaymentSystem(paymentSystemKey: PaymentSystemKey) {
   // TODO: REMOVE THIS WHEN TM TESTING FASE IS OVER
   const isEtmTestingUser = store.getters['permission/isEtmTestingUser'];
   if (isEtmTestingUser && paymentSystemKey === PaymentSystemKey.ETM) return true;

   const userTenant = store.state.userInfo?.userInfo?.tenant as Tenant | undefined;
   return userTenant?.payment_system?.key === paymentSystemKey;
}

export function getPaymentSystemOverviewDefaultRouteName(): string {
   if (checkPaymentSystem(PaymentSystemKey.ETM)) return 'RemunerationsOverview';
   return 'ProcessedContractsOverview';
}

export function getSelectablePaymentSystems(paymentSystems: Array<PaymentSystem>) {
   const tenantDefaultPaymentSystemId = store.state.userInfo?.userInfo?.tenant?.payment_system?.id;
   const isEtmTestingUser = !!store.getters['permission/isEtmTestingUser'];

   return paymentSystems.filter((paymentSystem) => {
      if (paymentSystem.key !== PaymentSystemKey.ETM) return true;
      if (paymentSystem.id === tenantDefaultPaymentSystemId) return true;
      return isEtmTestingUser;
   });
}

function normalizePaymentSystemId(paymentSystemId: unknown): number | undefined {
   const raw = Array.isArray(paymentSystemId) ? paymentSystemId[0] : paymentSystemId;
   const parsed = Number(raw);
   if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
   return parsed;
}

function getFallbackPaymentSystemId(selectablePaymentSystems: Array<PaymentSystem>): number | undefined {
   const tenantDefaultPaymentSystemId = store.state.userInfo?.userInfo?.tenant?.payment_system?.id;
   if (
      tenantDefaultPaymentSystemId &&
      selectablePaymentSystems.find((paymentSystem) => paymentSystem.id === tenantDefaultPaymentSystemId)
   ) {
      return tenantDefaultPaymentSystemId;
   }

   return selectablePaymentSystems[0]?.id;
}

async function getTenantSelectablePaymentSystems() {
   const existingPaymentSystems = store.state.paymentSystem?.paymentSystems || [];
   const paymentSystems =
      existingPaymentSystems.length > 0
         ? existingPaymentSystems
         : await store.dispatch('paymentSystem/getPaymentSystems', { query: { per_page: 999 } });

   return getSelectablePaymentSystems(paymentSystems || []);
}

function redirectWithPaymentSystemFilter(to: RouteLocationNormalized, paymentSystemId: number) {
   return {
      ...to,
      query: {
         ...to.query,
         payment_system_id: paymentSystemId.toString(),
      },
   };
}

export const PAYMENT_SYSTEM_FILTER_GUARD: NavigationGuard = async (to, from, next) => {
   await isUserInfoReady();

   const selectablePaymentSystems = await getTenantSelectablePaymentSystems();
   const fallbackPaymentSystemId = getFallbackPaymentSystemId(selectablePaymentSystems);

   if (!fallbackPaymentSystemId) {
      notifyError({ title: i18n.global.t('generic.access-denied') });
      return next('/');
   }

   const selectedPaymentSystemId = normalizePaymentSystemId(to.query.payment_system_id);
   const canUseSelectedPaymentSystem = selectedPaymentSystemId
      ? selectablePaymentSystems.some((paymentSystem) => paymentSystem.id === selectedPaymentSystemId)
      : false;

   if (canUseSelectedPaymentSystem) return next();

   return next(redirectWithPaymentSystemFilter(to, fallbackPaymentSystemId));
};

const PAYMENT_SYSTEM_GUARD = (paymentSystemKey: PaymentSystemKey | Array<PaymentSystemKey>): NavigationGuard => {
   return async (to, from, next) => {
      await isUserInfoReady();
      if (Array.isArray(paymentSystemKey)) {
         if (paymentSystemKey.some((paymentSystemKey) => checkPaymentSystem(paymentSystemKey))) return next();
      } else {
         if (checkPaymentSystem(paymentSystemKey)) return next();
      }
      notifyError({ title: i18n.global.t('generic.access-denied') });
      return next('/');
   };
};

export default PAYMENT_SYSTEM_GUARD;
